import React, { useState, useCallback, useEffect } from 'react';
import type { FC } from 'react';
import { isType, match, partial } from 'variant';
import { Box, InputState, SelectGroupsInput } from '@wix/cli-ui-kit';
import type { SelectOption, SelectGroup } from '@wix/cli-ui-kit';
import { useWixCliAstroBi } from '../../bi/index.js';
import { useGenerateDashboardPage } from './dashboard-page/index.js';
import { useGenerateDashboardModal } from './dashboard-modal/DashboardModal.js';
import { useGenerateServicePlugin } from './service-plugin/index.js';
import { useGenerateEmbeddedScript } from './embedded-script/index.js';
import { useGenerateDataCollection } from './data-collections/index.js';
import { DashboardPlugin } from './plugins/DashboardPlugin.js';
import { useGenerateCustomElement } from './custom-element/index.js';
import { useGenerateEditorReactComponent } from './editor-react-component/index.js';
import { useGenerateDashboardExternalPage } from './dashboard-external-page/index.js';
import { useGenerateEvent } from './event/index.js';
import { useGenerateAppTools } from './app-tools/index.js';
import { useWizardStep, Wizard } from './Wizard.js';
import { GenerateSitePlugin } from './site-plugin/index.js';
import { getGenerateFlowByDocsExtensionType } from './docsExtensionType.js';
import type { DocsExtensionType } from './docsExtensionType.js';
import { useGenerateFlows } from './generateFlows.js';
import type { GenerateFlow } from './generateFlows.js';

const FlowSelector: FC<{
  onSubmit: (flow: SelectOption<GenerateFlow>) => void;
  generateFlow: SelectOption<GenerateFlow>;
  generateFlows: Array<SelectGroup<GenerateFlow>>;
}> = ({ onSubmit, generateFlow, generateFlows }) => {
  const bi = useWixCliAstroBi();
  const { goToNextStep, isActive } = useWizardStep();

  const selectFlow = useCallback(
    (selectedType: SelectOption<GenerateFlow>) => {
      bi.cliFlowStepAnswered({
        question: 'What kind of extension would you like to generate?',
        questionKey: 'generate_command.flow.choose_flow',
        answer: selectedType.title,
      });
      onSubmit(selectedType);
      goToNextStep();
    },
    [bi, onSubmit, goToNextStep]
  );

  useEffect(() => {
    goToNextStep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SelectGroupsInput<GenerateFlow>
      value={
        !isActive
          ? InputState.Submitted(generateFlow)
          : InputState.NotSubmitted(generateFlow)
      }
      label={'What kind of extension would you like to generate?'}
      groups={generateFlows}
      onSubmit={selectFlow}
      maxHeight={15}
    />
  );
};

export const GenerateCommand: FC<{
  docsExtensionType?: DocsExtensionType;
}> = ({ docsExtensionType }) => {
  const bi = useWixCliAstroBi();

  const dashboardPageSteps = useGenerateDashboardPage();
  const dashboardModalSteps = useGenerateDashboardModal();
  const generateEventsSteps = useGenerateEvent();
  const generateDataCollectionSteps = useGenerateDataCollection();
  const generateServicePluginSteps = useGenerateServicePlugin();
  const generateEmbeddedScriptSteps = useGenerateEmbeddedScript();
  const generateCustomElementSteps = useGenerateCustomElement();
  const generateEditorReactComponentSteps = useGenerateEditorReactComponent();
  const generateDashboardExternalPageSteps = useGenerateDashboardExternalPage();
  const generateAppToolsSteps = useGenerateAppTools();

  const biCliFlowStepAnswered = useCallback(
    (selectedType: SelectOption<GenerateFlow>) => {
      bi.cliFlowStepAnswered({
        question: 'What kind of extension would you like to generate?',
        questionKey: 'generate_command.flow.choose_flow',
        answer: selectedType.title,
      });
    },
    [bi]
  );

  const [generateFlow, setGenerateFlow] = useState<
    SelectOption<GenerateFlow> | undefined
  >(() => {
    if (docsExtensionType) {
      const selectedType =
        getGenerateFlowByDocsExtensionType(docsExtensionType);
      biCliFlowStepAnswered(selectedType);
      return selectedType;
    }
    return;
  });

  const generateFlows = useGenerateFlows();

  const selectFlow = useCallback(
    (selectedType: SelectOption<GenerateFlow>) => {
      biCliFlowStepAnswered(selectedType);
      setGenerateFlow(selectedType);
    },
    [biCliFlowStepAnswered]
  );

  const shouldEditFlow =
    isType(generateFlow?.value, 'DashboardPage') ||
    isType(generateFlow?.value, 'DashboardModal') ||
    isType(generateFlow?.value, 'DashboardExternalPage') ||
    isType(generateFlow?.value, 'ServicePlugin') ||
    isType(generateFlow?.value, 'Event') ||
    isType(generateFlow?.value, 'EmbeddedScript') ||
    isType(generateFlow?.value, 'CustomElement') ||
    isType(generateFlow?.value, 'DataCollection') ||
    isType(generateFlow?.value, 'EditorReactComponent') ||
    isType(generateFlow?.value, 'AppTools');

  return (
    <Box flexDirection="column" paddingTop={1}>
      {!shouldEditFlow && (
        <SelectGroupsInput<GenerateFlow>
          value={
            generateFlow
              ? InputState.Submitted(generateFlow)
              : InputState.NotSubmitted(generateFlow)
          }
          label={'What kind of extension would you like to generate?'}
          groups={generateFlows}
          onSubmit={selectFlow}
          maxHeight={15}
        />
      )}
      {generateFlow &&
        match(
          generateFlow.value,
          partial({
            DashboardPage: () => (
              <Wizard
                name={generateFlow.value.type}
                steps={[
                  <FlowSelector
                    generateFlows={generateFlows}
                    generateFlow={generateFlow}
                    onSubmit={setGenerateFlow}
                  />,
                  ...dashboardPageSteps,
                ]}
              />
            ),
            DashboardModal: () => (
              <Wizard
                name={generateFlow.value.type}
                steps={[
                  <FlowSelector
                    generateFlows={generateFlows}
                    generateFlow={generateFlow}
                    onSubmit={setGenerateFlow}
                  />,
                  ...dashboardModalSteps,
                ]}
              />
            ),
            ServicePlugin: () => (
              <Wizard
                name={generateFlow.value.type}
                steps={[
                  <FlowSelector
                    generateFlows={generateFlows}
                    generateFlow={generateFlow}
                    onSubmit={setGenerateFlow}
                  />,
                  ...generateServicePluginSteps,
                ]}
              />
            ),
            Event: () => (
              <Wizard
                name={generateFlow.value.type}
                steps={[
                  <FlowSelector
                    generateFlows={generateFlows}
                    generateFlow={generateFlow}
                    onSubmit={setGenerateFlow}
                  />,
                  ...generateEventsSteps,
                ]}
              />
            ),
            DataCollection: () => (
              <Wizard
                name={generateFlow.value.type}
                steps={[
                  <FlowSelector
                    generateFlows={generateFlows}
                    generateFlow={generateFlow}
                    onSubmit={setGenerateFlow}
                  />,
                  ...generateDataCollectionSteps,
                ]}
              />
            ),
            EmbeddedScript: () => (
              <Wizard
                name={generateFlow.value.type}
                steps={[
                  <FlowSelector
                    generateFlows={generateFlows}
                    generateFlow={generateFlow}
                    onSubmit={setGenerateFlow}
                  />,
                  ...generateEmbeddedScriptSteps,
                ]}
              />
            ),
            CustomElement: () => (
              <Wizard
                name={generateFlow.value.type}
                steps={[
                  <FlowSelector
                    generateFlows={generateFlows}
                    generateFlow={generateFlow}
                    onSubmit={setGenerateFlow}
                  />,
                  ...generateCustomElementSteps,
                ]}
              />
            ),
            EditorReactComponent: () => (
              <Wizard
                name={generateFlow.value.type}
                steps={[
                  <FlowSelector
                    generateFlows={generateFlows}
                    generateFlow={generateFlow}
                    onSubmit={setGenerateFlow}
                  />,
                  ...generateEditorReactComponentSteps,
                ]}
              />
            ),
            DashboardExternalPage: () => (
              <Wizard
                name={generateFlow.value.type}
                steps={[
                  <FlowSelector
                    generateFlows={generateFlows}
                    generateFlow={generateFlow}
                    onSubmit={setGenerateFlow}
                  />,
                  ...generateDashboardExternalPageSteps,
                ]}
              />
            ),
            AppTools: () => (
              <Wizard
                name={generateFlow.value.type}
                steps={[
                  <FlowSelector
                    generateFlows={generateFlows}
                    generateFlow={generateFlow}
                    onSubmit={setGenerateFlow}
                  />,
                  ...generateAppToolsSteps,
                ]}
              />
            ),
            SitePlugin: () => <GenerateSitePlugin />,
            DashboardPlugin: () => (
              <DashboardPlugin flow={generateFlow.value} />
            ),
            DashboardMenuPlugin: () => (
              <DashboardPlugin flow={generateFlow.value} />
            ),
            default: () => null,
          })
        )}
    </Box>
  );
};
